import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  Copy,
  ExternalLink,
  KeyRound,
  Link2,
  Mail,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserRoundPlus,
  UsersRound,
  X,
} from "lucide-react";
import { supabase } from "../../../utils/supabase";
import { useLabPermissions } from "../../../hooks/useLabPermissions";

const STATUS_META = {
  active: {
    label: "Cuenta activa",
    className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    icon: CheckCircle2,
  },
  pending_confirmation: {
    label: "Falta confirmar",
    className: "border-amber-500/30 bg-amber-500/10 text-amber-200",
    icon: Clock3,
  },
  not_registered: {
    label: "Sin cuenta",
    className: "border-slate-500/30 bg-slate-500/10 text-slate-300",
    icon: KeyRound,
  },
  unavailable: {
    label: "Estado pendiente",
    className: "border-slate-500/30 bg-slate-500/10 text-slate-300",
    icon: Clock3,
  },
};

function formatDate(value, withTime = false) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    ...(withTime ? { timeStyle: "short" } : {}),
  }).format(date);
}

function formatClientNumber(clientNumber, branchNo) {
  if (!clientNumber) return "Sin número";
  return branchNo ? `${clientNumber} ${branchNo}` : String(clientNumber);
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.not_registered;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${meta.className}`}
    >
      <Icon size={13} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function Metric({ icon: Icon, label, value, tone = "sky" }) {
  const tones = {
    sky: "border-sky-500/20 bg-sky-500/10 text-sky-200",
    emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-200",
    amber: "border-amber-500/20 bg-amber-500/10 text-amber-200",
    slate: "border-white/10 bg-white/[0.035] text-slate-200",
  };

  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-2xl font-semibold text-white">{value}</div>
          <div className="mt-0.5 text-xs opacity-80">{label}</div>
        </div>
        <Icon size={22} className="opacity-80" aria-hidden="true" />
      </div>
    </div>
  );
}

export default function PortalUsersAdminPage() {
  const { isAdmin, isLabManager } = useLabPermissions();
  const canManage = isAdmin || isLabManager;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [migrationPending, setMigrationPending] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedClients, setExpandedClients] = useState({});
  const [portalLinkCopied, setPortalLinkCopied] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const searchTimer = useRef(null);
  const copyTimer = useRef(null);

  const portalLoginUrl = useMemo(
    () => `${window.location.origin}/portal/login`,
    []
  );

  const copyPortalLoginUrl = async () => {
    try {
      await navigator.clipboard.writeText(portalLoginUrl);
      setPortalLinkCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setPortalLinkCopied(false), 2500);
    } catch (error) {
      console.error("[PortalUsers] copy portal URL", error);
      setMessage({
        type: "error",
        text: "No se pudo copiar el enlace. Podés seleccionarlo y copiarlo manualmente.",
      });
    }
  };

  useEffect(
    () => () => {
      clearTimeout(copyTimer.current);
    },
    []
  );

  const load = useCallback(async () => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc(
        "admin_list_client_portal_accesses"
      );
      if (error) {
        const missingRpc =
          error?.code === "PGRST202" ||
          /admin_list_client_portal_accesses/i.test(
            String(error?.message || error)
          );
        if (!missingRpc) throw error;

        const { data: legacyData, error: legacyError } = await supabase
          .from("client_portal_users")
          .select(
            "id, email, created_at, client:clients(id, name, client_number, branch_no, cuit, tax_id)"
          )
          .order("created_at", { ascending: false });
        if (legacyError) throw legacyError;

        setRows(
          (legacyData || []).map((row) => ({
            access_id: row.id,
            email: row.email,
            client_id: row.client?.id,
            client_name: row.client?.name,
            client_number: row.client?.client_number,
            branch_no: row.client?.branch_no,
            cuit: row.client?.cuit || row.client?.tax_id,
            authorized_at: row.created_at,
            account_status: "unavailable",
            account_created_at: null,
            last_sign_in_at: null,
          }))
        );
        setMigrationPending(true);
        return;
      }
      setMigrationPending(false);
      setRows(data || []);
    } catch (error) {
      console.error("[PortalUsers] load", error);
      setMessage({
        type: "error",
        text:
          error?.message ||
          "No se pudieron cargar los accesos del portal de clientes.",
      });
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    const term = clientQuery.trim();
    if (term.length < 2 || selectedClient) {
      setClientOptions([]);
      return undefined;
    }

    searchTimer.current = setTimeout(async () => {
      const like = `%${term}%`;
      const { data, error } = await supabase
        .from("clients")
        .select("id, name, client_number, branch_no, cuit, tax_id")
        .or(
          [
            `name.ilike.${like}`,
            `cuit.ilike.${like}`,
            `tax_id.ilike.${like}`,
            `client_number.eq.${/^\d+$/.test(term) ? term : -1}`,
          ].join(",")
        )
        .order("name")
        .limit(10);

      if (error) {
        console.error("[PortalUsers] client search", error);
        setClientOptions([]);
        return;
      }
      setClientOptions(data || []);
    }, 280);

    return () => clearTimeout(searchTimer.current);
  }, [clientQuery, selectedClient]);

  const groups = useMemo(() => {
    const byClient = new Map();
    rows.forEach((row) => {
      if (!byClient.has(row.client_id)) {
        byClient.set(row.client_id, {
          id: row.client_id,
          name: row.client_name,
          clientNumber: row.client_number,
          branchNo: row.branch_no,
          cuit: row.cuit,
          accesses: [],
        });
      }
      byClient.get(row.client_id).accesses.push(row);
    });
    return Array.from(byClient.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "es", {
        sensitivity: "base",
      })
    );
  }, [rows]);

  const filteredGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return groups
      .map((group) => {
        const groupMatches =
          !needle ||
          [group.name, group.clientNumber, group.branchNo, group.cuit].some(
            (value) => String(value || "").toLowerCase().includes(needle)
          );
        const matchingAccesses = group.accesses.filter((access) => {
          const statusMatches =
            statusFilter === "all" || access.account_status === statusFilter;
          const emailMatches =
            !needle || String(access.email || "").toLowerCase().includes(needle);
          return statusMatches && (groupMatches || emailMatches);
        });
        return { ...group, accesses: matchingAccesses };
      })
      .filter((group) => group.accesses.length > 0);
  }, [groups, query, statusFilter]);

  const metrics = useMemo(
    () => ({
      companies: groups.length,
      emails: rows.length,
      active: rows.filter((row) => row.account_status === "active").length,
      pending: rows.filter((row) => row.account_status !== "active").length,
    }),
    [groups.length, rows]
  );

  const resetForm = () => {
    setEmail("");
    setClientQuery("");
    setClientOptions([]);
    setSelectedClient(null);
  };

  const openForClient = (group) => {
    setSelectedClient({
      id: group.id,
      name: group.name,
      client_number: group.clientNumber,
      branch_no: group.branchNo,
      cuit: group.cuit,
    });
    setClientQuery(group.name || "");
    setEmail("");
    setMessage(null);
    setFormOpen(true);
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    setMessage(null);
    const normalizedEmail = email.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setMessage({ type: "error", text: "Ingresá un email válido." });
      return;
    }
    if (!selectedClient) {
      setMessage({
        type: "error",
        text: "Elegí la empresa a la que pertenece el correo.",
      });
      return;
    }

    try {
      setBusyKey("create");
      const { error } = await supabase.rpc(
        "admin_create_client_portal_access",
        {
          p_client_id: selectedClient.id,
          p_email: normalizedEmail,
        }
      );
      if (error) {
        const missingRpc =
          error?.code === "PGRST202" ||
          /admin_create_client_portal_access/i.test(
            String(error?.message || error)
          );
        if (!missingRpc) throw error;

        const { data: authData } = await supabase.auth.getUser();
        const { error: legacyError } = await supabase
          .from("client_portal_users")
          .insert({
            email: normalizedEmail,
            client_id: selectedClient.id,
            created_by: authData?.user?.id || null,
          });
        if (legacyError) throw legacyError;
      }

      setMessage({
        type: "ok",
        text: `${normalizedEmail} quedó autorizado para ${selectedClient.name}.`,
      });
      setFormOpen(false);
      resetForm();
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text:
          /already|duplicate|autorizado|23505/i.test(
            String(error?.message || error)
          )
            ? "Ese email ya está autorizado para un cliente."
            : error?.message || "No se pudo autorizar el correo.",
      });
    } finally {
      setBusyKey("");
    }
  };

  const handleRevoke = async (access, group) => {
    if (
      !window.confirm(
        `¿Quitar el acceso de ${access.email}?\n\nDejará de ver los certificados de ${group.name}. La cuenta no se elimina y se puede volver a autorizar más adelante.`
      )
    ) {
      return;
    }

    try {
      setBusyKey(access.access_id);
      setMessage(null);
      const { error } = await supabase.rpc(
        "admin_revoke_client_portal_access",
        { p_access_id: access.access_id }
      );
      if (error) throw error;

      setMessage({
        type: "ok",
        text: `${access.email} ya no tiene acceso al portal de ${group.name}.`,
      });
      await load();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "No se pudo quitar el acceso.",
      });
    } finally {
      setBusyKey("");
    }
  };

  const toggleClient = (clientId) => {
    setExpandedClients((current) => ({
      ...current,
      [clientId]: current[clientId] === false,
    }));
  };

  if (!canManage) {
    return (
      <div className="mx-auto max-w-3xl py-10">
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-5 text-rose-200">
          Solo administración y el jefe de laboratorio pueden gestionar los
          accesos del portal de clientes.
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 pb-16">
      <header className="flex flex-col gap-4 py-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <ShieldCheck className="text-sky-400" size={26} />
            Accesos al portal de clientes
          </h1>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">
            Cada correo autorizado puede crear o recuperar su contraseña y ver
            únicamente los certificados de la empresa vinculada.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            resetForm();
            setMessage(null);
            setFormOpen(true);
          }}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-500"
        >
          <Plus size={17} />
          Autorizar correo
        </button>
      </header>

      <section className="rounded-xl border border-sky-500/25 bg-sky-500/[0.07] p-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 rounded-lg bg-sky-500/15 p-2 text-sky-300">
            <Link2 size={19} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-white">
              Enlace de acceso para clientes
            </div>
            <p className="mt-1 text-sm leading-5 text-slate-400">
              Compartí este enlace con los clientes autorizados para que puedan
              ingresar o crear su contraseña por primera vez.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
              <a
                href={portalLoginUrl}
                target="_blank"
                rel="noreferrer"
                className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-white/10 bg-slate-950/50 px-3 py-2.5 font-mono text-sm text-sky-200 hover:border-sky-500/40 hover:text-sky-100"
                title="Abrir el portal de clientes"
              >
                <span className="truncate">{portalLoginUrl}</span>
                <ExternalLink size={15} className="shrink-0" aria-hidden="true" />
              </a>
              <button
                type="button"
                onClick={copyPortalLoginUrl}
                className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition ${
                  portalLinkCopied
                    ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-200"
                    : "border-sky-500/30 bg-sky-600 text-white hover:bg-sky-500"
                }`}
              >
                {portalLinkCopied ? (
                  <CheckCircle2 size={17} aria-hidden="true" />
                ) : (
                  <Copy size={17} aria-hidden="true" />
                )}
                {portalLinkCopied ? "Enlace copiado" : "Copiar enlace"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {message && (
        <div
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            message.type === "ok"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              : "border-rose-500/30 bg-rose-500/10 text-rose-200"
          }`}
        >
          <span>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage(null)}
            className="shrink-0 opacity-70 hover:opacity-100"
            aria-label="Cerrar mensaje"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {migrationPending && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm leading-6 text-amber-100">
          <span className="font-semibold">Actualización de base pendiente.</span>{" "}
          Las empresas y correos se muestran normalmente, pero el estado de las
          cuentas y la baja precisa de accesos se habilitarán al aplicar la
          migración de gestión del portal.
        </div>
      )}

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric
          icon={Building2}
          label="Empresas con acceso"
          value={metrics.companies}
          tone="sky"
        />
        <Metric
          icon={Mail}
          label="Correos autorizados"
          value={metrics.emails}
          tone="slate"
        />
        <Metric
          icon={CheckCircle2}
          label="Cuentas activas"
          value={metrics.active}
          tone="emerald"
        />
        <Metric
          icon={Clock3}
          label="Aún sin activar"
          value={metrics.pending}
          tone="amber"
        />
      </section>

      <section className="grid grid-cols-1 gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 md:grid-cols-[1fr_220px]">
        <label className="relative">
          <Search
            size={17}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar empresa, N° de cliente, CUIT o correo…"
            className="w-full rounded-lg border border-white/10 bg-slate-900/60 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-sky-500/50 focus:outline-none"
          />
        </label>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2.5 text-sm text-white focus:border-sky-500/50 focus:outline-none"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Cuenta activa</option>
          <option value="pending_confirmation">Falta confirmar</option>
          <option value="not_registered">Sin cuenta</option>
        </select>
      </section>

      <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.07] px-4 py-3 text-sm text-sky-100">
        <span className="font-semibold">Flujo recomendado:</span> autorizá el
        correo aquí, compartile el enlace de acceso que aparece arriba y pedile
        que seleccione <span className="font-medium text-white">Primera vez</span>.
        Si ya tenía cuenta, el acceso se vincula automáticamente.
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-slate-400">
          Cargando accesos…
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-5 py-10 text-center">
          <UsersRound className="mx-auto text-slate-600" size={34} />
          <div className="mt-3 text-sm font-medium text-slate-200">
            {rows.length === 0
              ? "Todavía no hay correos autorizados"
              : "No hay resultados para esta búsqueda"}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {rows.length === 0
              ? "Empezá autorizando el primer correo de una empresa."
              : "Probá con otro texto o cambiá el filtro de estado."}
          </p>
        </div>
      ) : (
        <section className="space-y-3">
          {filteredGroups.map((group) => {
            const expanded = expandedClients[group.id] !== false;
            return (
              <article
                key={group.id}
                className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.025]"
              >
                <div className="flex flex-col gap-3 border-b border-white/10 px-4 py-4 md:flex-row md:items-center md:justify-between">
                  <button
                    type="button"
                    onClick={() => toggleClient(group.id)}
                    className="flex min-w-0 items-start gap-3 text-left"
                  >
                    <span className="mt-0.5 rounded-lg bg-sky-500/10 p-2 text-sky-300">
                      <Building2 size={19} />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-white">
                        {group.name || "Empresa sin nombre"}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                        <span>
                          Cliente N°{" "}
                          <b className="font-medium text-slate-200">
                            {formatClientNumber(
                              group.clientNumber,
                              group.branchNo
                            )}
                          </b>
                        </span>
                        <span>
                          CUIT{" "}
                          <b className="font-medium text-slate-200">
                            {group.cuit || "Sin informar"}
                          </b>
                        </span>
                        <span>
                          {group.accesses.length}{" "}
                          {group.accesses.length === 1
                            ? "correo visible"
                            : "correos visibles"}
                        </span>
                      </span>
                    </span>
                  </button>

                  <div className="flex items-center gap-2 self-end md:self-auto">
                    <button
                      type="button"
                      onClick={() => openForClient(group)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500/30 px-3 py-2 text-xs font-medium text-sky-200 hover:bg-sky-500/10"
                    >
                      <UserRoundPlus size={15} />
                      Agregar correo
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleClient(group.id)}
                      className="rounded-lg border border-white/10 p-2 text-slate-400 hover:bg-white/5 hover:text-white"
                      aria-label={
                        expanded ? "Contraer empresa" : "Expandir empresa"
                      }
                    >
                      {expanded ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="divide-y divide-white/[0.07]">
                    {group.accesses.map((access) => (
                      <div
                        key={access.access_id}
                        className="grid grid-cols-1 gap-3 px-4 py-3 md:grid-cols-[minmax(230px,1fr)_170px_180px_auto] md:items-center"
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <Mail
                            size={16}
                            className="shrink-0 text-slate-500"
                          />
                          <span className="truncate text-sm font-medium text-white">
                            {access.email}
                          </span>
                        </div>
                        <StatusBadge status={access.account_status} />
                        <div className="text-xs text-slate-500">
                          {access.last_sign_in_at ? (
                            <>
                              Último ingreso{" "}
                              <span className="text-slate-300">
                                {formatDate(access.last_sign_in_at, true)}
                              </span>
                            </>
                          ) : (
                            <>
                              Autorizado{" "}
                              <span className="text-slate-300">
                                {formatDate(access.authorized_at)}
                              </span>
                            </>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRevoke(access, group)}
                          disabled={
                            migrationPending || busyKey === access.access_id
                          }
                          title={
                            migrationPending
                              ? "Disponible después de actualizar la base de datos"
                              : "Quitar acceso"
                          }
                          className="inline-flex items-center justify-center gap-1.5 justify-self-start rounded-lg border border-rose-500/30 px-2.5 py-1.5 text-xs text-rose-300 hover:bg-rose-500/10 disabled:opacity-50 md:justify-self-end"
                        >
                          <Trash2 size={14} />
                          {busyKey === access.access_id
                            ? "Quitando…"
                            : "Quitar"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 px-4 py-6">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b] shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Autorizar un correo
                </h2>
                <p className="mt-1 text-xs text-slate-400">
                  El correo quedará vinculado a una sola empresa.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormOpen(false);
                  resetForm();
                }}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-white/5 hover:text-white"
                aria-label="Cerrar"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAdd} className="space-y-4 p-5">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-200">
                  Empresa
                </label>
                {selectedClient ? (
                  <div className="flex items-start justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-white">
                        {selectedClient.name}
                      </div>
                      <div className="mt-1 text-xs text-emerald-100/70">
                        Cliente N°{" "}
                        {formatClientNumber(
                          selectedClient.client_number,
                          selectedClient.branch_no
                        )}{" "}
                        · CUIT{" "}
                        {selectedClient.cuit ||
                          selectedClient.tax_id ||
                          "Sin informar"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedClient(null);
                        setClientQuery("");
                      }}
                      className="shrink-0 text-emerald-200/70 hover:text-white"
                      aria-label="Cambiar empresa"
                    >
                      <X size={17} />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search
                      size={17}
                      className="pointer-events-none absolute left-3 top-3 text-slate-500"
                    />
                    <input
                      value={clientQuery}
                      onChange={(event) => setClientQuery(event.target.value)}
                      placeholder="Nombre, N° de cliente o CUIT…"
                      autoFocus
                      className="w-full rounded-xl border border-white/10 bg-slate-900/70 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-sky-500/50 focus:outline-none"
                    />
                    {clientOptions.length > 0 && (
                      <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-white/10 bg-[#121820] p-1 shadow-2xl">
                        {clientOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                              setSelectedClient(option);
                              setClientQuery(option.name || "");
                              setClientOptions([]);
                            }}
                            className="block w-full rounded-lg px-3 py-2.5 text-left hover:bg-white/[0.07]"
                          >
                            <span className="block truncate text-sm font-medium text-white">
                              {option.name}
                            </span>
                            <span className="mt-0.5 block text-xs text-slate-500">
                              Cliente N°{" "}
                              {formatClientNumber(
                                option.client_number,
                                option.branch_no
                              )}{" "}
                              · CUIT{" "}
                              {option.cuit ||
                                option.tax_id ||
                                "Sin informar"}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-200">
                  Correo autorizado
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="nombre@empresa.com"
                  className="w-full rounded-xl border border-white/10 bg-slate-900/70 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-sky-500/50 focus:outline-none"
                />
                <p className="mt-1.5 text-xs leading-5 text-slate-500">
                  Puede ser un contacto, responsable de calidad o cualquier
                  persona autorizada por la empresa.
                </p>
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-white/10 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setFormOpen(false);
                    resetForm();
                  }}
                  className="rounded-lg border border-white/15 px-4 py-2.5 text-sm text-slate-300 hover:bg-white/5"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={busyKey === "create"}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60"
                >
                  <ShieldCheck size={17} />
                  {busyKey === "create" ? "Autorizando…" : "Autorizar acceso"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
